import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardHeader,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Image,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Select,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../services/api';
import { CreatableEmailKind, EmailButton, EventEmail, EventEmailContent } from '../../types/api';
import {
  BUTTON_COLOURS,
  BUTTON_TARGETS,
  insertToken,
  MergeFieldInfo,
  normaliseContent,
} from '../../utils/emailEditor';
import BlockEditor from './BlockEditor';
import { setEmailPalette } from '../../utils/emailBlocks';
import { MAX_TEXT_CHARS, textLength } from '../../utils/emailBlocks';
import RichTextField from './RichTextField';
import SendPanel from './SendPanel';

const SYSTEM_KINDS = ['invitation', 'reminder', 'final_confirmation', 'rsvp_confirmation', 'rsvp_absence'];
const NEW_EMAIL_KINDS: { kind: CreatableEmailKind; label: string }[] = [
  { kind: 'save_the_date', label: 'Save the date' },
  { kind: 'follow_up', label: 'Relance / suivi' },
  { kind: 'cancellation', label: 'Annulation' },
  { kind: 'custom', label: 'E-mail personnalisé' },
];

const MAX_BLOCKS = 3;
const MAX_BUTTONS = 3;

const errorText = (err: any): string => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg).join(' ; ');
  return err?.message || 'Erreur inconnue';
};

const toContent = (e: EventEmail): EventEmailContent => ({
  name: e.name,
  subject: e.subject,
  title: e.title,
  blocks: [...e.blocks],
  buttons: e.buttons.map((b) => ({ ...b })),
  content: e.content ? JSON.parse(JSON.stringify(e.content)) : null,
});

const lastEdit = (e: EventEmail) =>
  e.updated_at
    ? `Modifié le ${new Date(e.updated_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}`
    : 'Version par défaut';

// --- Plain-text input with an "Insérer un champ" menu (subject, title, button label) ---

const TokenInput: React.FC<{
  label: string;
  value: string;
  fields: MergeFieldInfo[];
  onChange: (v: string) => void;
  testId?: string;
}> = ({ label, value, fields, onChange, testId }) => {
  const ref = useRef<HTMLInputElement>(null);
  const caret = useRef<[number, number]>([value.length, value.length]);
  const remember = () => {
    const el = ref.current;
    if (el) caret.current = [el.selectionStart ?? value.length, el.selectionEnd ?? value.length];
  };
  const insert = (key: string) => {
    const next = insertToken(value, key, caret.current[0], caret.current[1]);
    onChange(next.value);
    caret.current = [next.caret, next.caret];
    requestAnimationFrame(() => {
      ref.current?.focus();
      ref.current?.setSelectionRange(next.caret, next.caret);
    });
  };
  return (
    <FormControl>
      <HStack justify="space-between" mb={1}>
        <FormLabel m={0} fontSize="sm">{label}</FormLabel>
        <Menu>
          <MenuButton as={Button} size="xs" variant="outline" rightIcon={<ChevronDownIcon />} onMouseDown={remember}>
            Insérer un champ
          </MenuButton>
          <MenuList>
            {fields.map((f) => (
              <MenuItem key={f.key} fontSize="sm" onClick={() => insert(f.key)}>{f.label}</MenuItem>
            ))}
          </MenuList>
        </Menu>
      </HStack>
      <Input
        ref={ref}
        size="sm"
        bg="white"
        value={value}
        data-testid={testId}
        onChange={(e) => { onChange(e.target.value); remember(); }}
        onKeyUp={remember}
        onClick={remember}
        onBlur={remember}
      />
    </FormControl>
  );
};

// --- Branding (header / footer images shared by all the event's emails) ---

const BrandingSlot: React.FC<{ eventId: number; slot: 'header' | 'footer'; label: string; url?: string; custom: boolean }> = ({
  eventId, slot, label, url, custom,
}) => {
  const toast = useToast();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const upload = useMutation({
    mutationFn: (file: File) => apiService.uploadEmailImage(eventId, slot, file),
    onSuccess: (data) => {
      qc.setQueryData(['email-branding', eventId], data);
      toast({ title: `${label} remplacé`, status: 'success', duration: 3000 });
    },
    onError: (err) => toast({ title: 'Image refusée', description: errorText(err), status: 'error', duration: 6000 }),
  });
  const reset = useMutation({
    mutationFn: () => apiService.resetEmailImage(eventId, slot),
    onSuccess: (data) => {
      qc.setQueryData(['email-branding', eventId], data);
      toast({ title: `${label} : image FFM par défaut rétablie`, status: 'success', duration: 3000 });
    },
    onError: (err) => toast({ title: 'Échec', description: errorText(err), status: 'error', duration: 6000 }),
  });
  return (
    <Box flex="1" minW={0}>
      <Text fontSize="sm" fontWeight="semibold" mb={1}>
        {label} {custom ? <Badge colorScheme="blue">Personnalisé</Badge> : <Badge>Par défaut</Badge>}
      </Text>
      <Box borderWidth="1px" borderRadius="md" bg="gray.50" minH="90px" overflow="hidden" display="flex" alignItems="center" justifyContent="center">
        {url ? <Image src={url} alt={label} w="100%" h="auto" maxH="260px" objectFit="contain" data-testid={`branding-${slot}-thumb`} /> : <Spinner size="sm" />}
      </Box>
      <HStack mt={2}>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg"
          style={{ display: 'none' }}
          data-testid={`branding-${slot}-input`}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) upload.mutate(f);
            e.target.value = '';
          }}
        />
        <Button size="xs" onClick={() => fileRef.current?.click()} isLoading={upload.isPending}>Remplacer</Button>
        {custom && <Button size="xs" variant="ghost" onClick={() => reset.mutate()} isLoading={reset.isPending}>Rétablir par défaut</Button>}
      </HStack>
    </Box>
  );
};

// --- Confirm dialog (reset, discard unsaved changes) ---

interface Confirm {
  title: string;
  body: string;
  confirmLabel: string;
  onConfirm: () => void;
}

const ConfirmDialog: React.FC<{ confirm: Confirm | null; onClose: () => void }> = ({ confirm, onClose }) => {
  const cancelRef = useRef<HTMLButtonElement>(null);
  return (
    <AlertDialog isOpen={!!confirm} leastDestructiveRef={cancelRef} onClose={onClose} isCentered>
      <AlertDialogOverlay>
        <AlertDialogContent mx={4}>
          <AlertDialogHeader fontSize="lg">{confirm?.title}</AlertDialogHeader>
          <AlertDialogBody>{confirm?.body}</AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={onClose}>Annuler</Button>
            <Button colorScheme="red" ml={3} onClick={() => { confirm?.onConfirm(); onClose(); }} data-testid="confirm-dialog-ok">
              {confirm?.confirmLabel}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

// --- Live preview (rendered by the backend, shown in a sandboxed iframe) ---

const Preview: React.FC<{ eventId: number; emailId: number; draft: EventEmailContent }> = ({ eventId, emailId, draft }) => {
  const [mode, setMode] = useState<'desktop' | 'mobile'>('desktop');
  const [state, setState] = useState<{ html?: string; subject?: string; error?: string; loading: boolean }>({ loading: true });
  const key = useMemo(() => JSON.stringify(draft), [draft]);
  const seq = useRef(0);

  useEffect(() => {
    const mine = ++seq.current;
    setState((s) => ({ ...s, loading: true }));
    const timer = setTimeout(async () => {
      try {
        const res = await apiService.previewEventEmail(eventId, emailId, JSON.parse(key));
        if (mine === seq.current) setState({ html: res.html, subject: res.subject, loading: false });
      } catch (err) {
        if (mine === seq.current) setState((s) => ({ ...s, error: errorText(err), loading: false }));
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [eventId, emailId, key]);

  return (
    <Box data-testid="email-preview">
      <Flex justify="space-between" align="center" mb={2} wrap="wrap" gap={2}>
        <HStack>
          <Heading size="sm">Aperçu</Heading>
          {state.loading && <Spinner size="xs" />}
        </HStack>
        <ButtonGroup size="xs" isAttached variant="outline">
          <Button isActive={mode === 'desktop'} onClick={() => setMode('desktop')}>Ordinateur</Button>
          <Button isActive={mode === 'mobile'} onClick={() => setMode('mobile')}>Mobile</Button>
        </ButtonGroup>
      </Flex>
      {state.error && (
        <Alert status="warning" size="sm" mb={2} borderRadius="md">
          <AlertIcon />
          <Text fontSize="sm">{state.error}</Text>
        </Alert>
      )}
      {state.subject !== undefined && (
        <Text fontSize="sm" mb={2} color="gray.600">
          <b>Objet :</b> {state.subject}
        </Text>
      )}
      <Box borderWidth="1px" borderRadius="md" bg="gray.100" p={2} overflow="auto">
        <Box mx="auto" w={mode === 'mobile' ? '375px' : '100%'} maxW="100%" transition="width 0.2s">
          <iframe
            title="Aperçu de l'e-mail"
            sandbox=""
            srcDoc={state.html || ''}
            style={{ width: '100%', height: mode === 'mobile' ? '640px' : '720px', border: 0, background: 'white' }}
          />
        </Box>
      </Box>
      <Text fontSize="xs" color="gray.500" mt={1}>
        Valeurs d'exemple : premier invité de l'événement, ou une personne fictive s'il n'y en a pas.
      </Text>
    </Box>
  );
};

// --- Editor for one email ---

const Editor: React.FC<{
  eventId: number;
  email: EventEmail;
  fields: MergeFieldInfo[];
  draft: EventEmailContent;
  setDraft: (d: EventEmailContent) => void;
  dirty: boolean;
  saving: boolean;
  onSave: () => void;
  onReset: () => void;
  onTest: () => void;
  testing: boolean;
  onDelete?: () => void;
  headerUrl?: string;
  footerUrl?: string;
}> = ({ eventId, email, fields, draft, setDraft, dirty, saving, onSave, onReset, onTest, testing, onDelete, headerUrl, footerUrl }) => {
  // App-wide palette (#22): the editor shows the live colours. Set before the editor renders; re-key it when they change.
  const paletteQ = useQuery({ queryKey: ['email-palette'], queryFn: () => apiService.getEmailPalette(), staleTime: 0 });
  if (paletteQ.data) setEmailPalette(paletteQ.data);
  const paletteKey = paletteQ.data ? Object.values(paletteQ.data).join('') : 'default';
  const patch = (p: Partial<EventEmailContent>) => setDraft({ ...draft, ...p });
  const setButton = (i: number, p: Partial<EmailButton>) =>
    patch({ buttons: draft.buttons.map((b, j) => (j === i ? { ...b, ...p } : b)) });
  const tooLong = (draft.content || []).some((b) => b.type === 'text' && textLength(b.paragraphs) > MAX_TEXT_CHARS);
  const knownColour = (c?: string | null) => BUTTON_COLOURS.some((o) => o.value === (c || '').toLowerCase());

  return (
    <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={6} alignItems="start">
      <VStack align="stretch" spacing={4} data-testid="email-editor">
        <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
          <Heading size="sm">{email.name}</Heading>
          <HStack>
            <Button
              size="sm"
              variant="outline"
              onClick={onTest}
              isDisabled={dirty}
              isLoading={testing}
              title={dirty ? 'Enregistrez d\'abord : le test envoie la version enregistrée' : 'Envoie la version enregistrée à votre adresse e-mail'}
              data-testid="email-test-send"
            >
              M'envoyer un test
            </Button>
            <Button size="sm" variant="outline" colorScheme="red" onClick={onReset} data-testid="email-reset">Réinitialiser</Button>
            {onDelete && (
              <Button size="sm" variant="outline" colorScheme="red" onClick={onDelete} data-testid="email-delete">Supprimer</Button>
            )}
            <Button size="sm" colorScheme="blue" onClick={onSave} isDisabled={!dirty || tooLong} isLoading={saving} title={tooLong ? `Un bloc de texte dépasse ${MAX_TEXT_CHARS} caractères` : undefined} data-testid="email-save">
              Enregistrer
            </Button>
          </HStack>
        </Flex>
        {dirty && (
          <Alert status="info" borderRadius="md" py={2} data-testid="unsaved-banner">
            <AlertIcon />
            <Text fontSize="sm">Modifications non enregistrées.</Text>
          </Alert>
        )}

        <TokenInput label="Objet" value={draft.subject} fields={fields} onChange={(v) => patch({ subject: v })} testId="email-subject" />
        {draft.content ? (
          <BlockEditor key={paletteKey} blocks={draft.content} fields={fields} resetKey={email.id} headerUrl={headerUrl} footerUrl={footerUrl} allowQr={email.kind === 'final_confirmation'} onChange={(content) => patch({ content })} />
        ) : (
          <>
        <TokenInput label="Titre" value={draft.title} fields={fields} onChange={(v) => patch({ title: v })} testId="email-title" />

        {draft.blocks.map((b, i) => (
          <RichTextField
            key={`${email.id}-${i}`}
            testId={`email-block-${i}`}
            label={`Texte ${i + 1}`}
            value={b}
            fields={fields}
            onChange={(v) => patch({ blocks: draft.blocks.map((x, j) => (j === i ? v : x)) })}
            onRemove={draft.blocks.length > 1 ? () => patch({ blocks: draft.blocks.filter((_, j) => j !== i) }) : undefined}
          />
        ))}
        {draft.blocks.length < MAX_BLOCKS && (
          <Button size="sm" variant="outline" alignSelf="flex-start" onClick={() => patch({ blocks: [...draft.blocks, ''] })}>
            Ajouter un texte
          </Button>
        )}

        <Box>
          <Heading size="xs" mb={2}>Boutons</Heading>
          <VStack align="stretch" spacing={3}>
            {draft.buttons.map((b, i) => (
              <Card key={i} variant="outline" size="sm" data-testid={`email-button-${i}`}>
                <CardBody>
                  <VStack align="stretch" spacing={3}>
                    <TokenInput label="Libellé" value={b.label} fields={fields} onChange={(v) => setButton(i, { label: v })} />
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                      <FormControl>
                        <FormLabel fontSize="sm" mb={1}>Couleur</FormLabel>
                        <HStack>
                          <Box w="20px" h="20px" borderRadius="sm" borderWidth="1px" bg={knownColour(b.colour) ? (b.colour as string) : '#ff0e0e'} flexShrink={0} />
                          <Select size="sm" bg="white" value={knownColour(b.colour) ? (b.colour || '').toLowerCase() : '#ff0e0e'} onChange={(e) => setButton(i, { colour: e.target.value })}>
                            {BUTTON_COLOURS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                          </Select>
                        </HStack>
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm" mb={1}>Le bouton ouvre</FormLabel>
                        <Select size="sm" bg="white" value={b.target} onChange={(e) => setButton(i, { target: e.target.value as EmailButton['target'] })}>
                          {BUTTON_TARGETS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </Select>
                      </FormControl>
                    </SimpleGrid>
                    {b.target === 'external' && (
                      <FormControl>
                        <FormLabel fontSize="sm" mb={1}>Adresse du lien (https://)</FormLabel>
                        <Input size="sm" bg="white" value={b.url || ''} placeholder="https://" onChange={(e) => setButton(i, { url: e.target.value })} />
                      </FormControl>
                    )}
                    <Button size="xs" variant="ghost" colorScheme="red" alignSelf="flex-end" onClick={() => patch({ buttons: draft.buttons.filter((_, j) => j !== i) })}>
                      Supprimer ce bouton
                    </Button>
                  </VStack>
                </CardBody>
              </Card>
            ))}
            {draft.buttons.length < MAX_BUTTONS && (
              <Button
                size="sm"
                variant="outline"
                alignSelf="flex-start"
                onClick={() => patch({ buttons: [...draft.buttons, { label: 'Nouveau bouton', colour: '#ff0e0e', target: 'rsvp_form', url: null }] })}
              >
                Ajouter un bouton
              </Button>
            )}
          </VStack>
        </Box>
          </>
        )}
      </VStack>
      <Preview eventId={eventId} emailId={email.id} draft={draft} />
    </SimpleGrid>
  );
};

// --- The tab ---

const EmailsTab: React.FC<{ eventId: number }> = ({ eventId }) => {
  const toast = useToast();
  const qc = useQueryClient();
  const emailsQ = useQuery({ queryKey: ['event-emails', eventId], queryFn: () => apiService.getEventEmails(eventId) });
  const brandingQ = useQuery({ queryKey: ['email-branding', eventId], queryFn: () => apiService.getEmailBranding(eventId) });
  const fieldsQ = useQuery({ queryKey: ['email-merge-fields', eventId], queryFn: () => apiService.getEmailMergeFields(eventId), staleTime: Infinity });

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [draft, setDraft] = useState<EventEmailContent | null>(null);
  const [baseline, setBaseline] = useState<string>('');
  const [confirm, setConfirm] = useState<Confirm | null>(null);

  const emails = emailsQ.data;
  const selected = emails?.find((e) => e.id === selectedId) || null;
  const fields = fieldsQ.data;

  const open = (e: EventEmail) => {
    setSelectedId(e.id);
    const c = toContent(e);
    setDraft(c);
    setBaseline(normaliseContent(c));
  };

  useEffect(() => {
    if (emails && emails.length && selectedId === null) open(emails[0]);
  }, [emails]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = !!draft && normaliseContent(draft) !== baseline;

  useEffect(() => {
    if (!dirty) return;
    const handler = (ev: BeforeUnloadEvent) => { ev.preventDefault(); ev.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const replaceInCache = (saved: EventEmail) =>
    qc.setQueryData<EventEmail[]>(['event-emails', eventId], (old) => (old || []).map((e) => (e.id === saved.id ? saved : e)));

  const save = useMutation({
    mutationFn: () => apiService.saveEventEmail(eventId, selectedId as number, draft as EventEmailContent),
    onSuccess: (saved) => {
      replaceInCache(saved);
      const c = toContent(saved);
      setDraft(c);
      setBaseline(normaliseContent(c));
      toast({ title: 'E-mail enregistré', status: 'success', duration: 3000 });
    },
    onError: (err) => toast({ title: 'Enregistrement refusé', description: errorText(err), status: 'error', duration: 8000 }),
  });

  const reset = useMutation({
    mutationFn: () => apiService.resetEventEmail(eventId, selectedId as number),
    onSuccess: (saved) => {
      replaceInCache(saved);
      const c = toContent(saved);
      setDraft(c);
      setBaseline(normaliseContent(c));
      toast({ title: 'Version par défaut rétablie', status: 'success', duration: 3000 });
    },
    onError: (err) => toast({ title: 'Échec de la réinitialisation', description: errorText(err), status: 'error', duration: 8000 }),
  });

  const testSend = useMutation({
    mutationFn: () => apiService.sendTestEventEmail(eventId, selectedId as number),
    onSuccess: (res) => toast({ title: 'Test envoyé', description: `Envoyé à ${res.sent_to}`, status: 'success', duration: 5000 }),
    onError: (err) => toast({ title: 'Test non envoyé', description: errorText(err), status: 'error', duration: 8000 }),
  });

  const create = useMutation({
    mutationFn: (kind: CreatableEmailKind) => apiService.createEventEmail(eventId, kind),
    onSuccess: (created) => {
      qc.setQueryData<EventEmail[]>(['event-emails', eventId], (old) => [...(old || []), created]);
      open(created);
      toast({ title: 'E-mail ajouté', description: "Modifiez-le puis enregistrez avant de l'envoyer.", status: 'success', duration: 5000 });
    },
    onError: (err) => toast({ title: 'Création refusée', description: errorText(err), status: 'error', duration: 8000 }),
  });

  const remove = useMutation({
    mutationFn: (id: number) => apiService.deleteEventEmail(eventId, id),
    onSuccess: (_d, id) => {
      const rest = (qc.getQueryData<EventEmail[]>(['event-emails', eventId]) || []).filter((e) => e.id !== id);
      qc.setQueryData<EventEmail[]>(['event-emails', eventId], rest);
      if (rest.length) open(rest[0]);
      toast({ title: 'E-mail supprimé', status: 'success', duration: 3000 });
    },
    onError: (err) => toast({ title: 'Suppression refusée', description: errorText(err), status: 'error', duration: 8000 }),
  });

  const choose = (e: EventEmail) => {
    if (e.id === selectedId) return;
    if (!dirty) return open(e);
    setConfirm({
      title: 'Modifications non enregistrées',
      body: 'Si vous changez d\'e-mail maintenant, vos modifications seront perdues.',
      confirmLabel: 'Abandonner les modifications',
      onConfirm: () => open(e),
    });
  };

  const askNew = (kind: CreatableEmailKind) => {
    if (!dirty) return create.mutate(kind);
    setConfirm({
      title: 'Modifications non enregistrées',
      body: 'Si vous ajoutez un e-mail maintenant, vos modifications seront perdues.',
      confirmLabel: 'Abandonner les modifications',
      onConfirm: () => create.mutate(kind),
    });
  };

  const askDelete = (e: EventEmail) =>
    setConfirm({
      title: `Supprimer « ${e.name} » ?`,
      body: "L'e-mail est supprimé. L'historique de ses envois est conservé.",
      confirmLabel: 'Supprimer',
      onConfirm: () => remove.mutate(e.id),
    });

  const askReset = () =>
    setConfirm({
      title: 'Réinitialiser cet e-mail ?',
      body: 'Le texte, le titre, l\'objet et les boutons reviennent à la version FFM par défaut. Cette action est immédiate.',
      confirmLabel: 'Réinitialiser',
      onConfirm: () => reset.mutate(),
    });

  if (emailsQ.isLoading || fieldsQ.isLoading) {
    return <Flex justify="center" py={10}><Spinner /></Flex>;
  }
  if (emailsQ.isError || fieldsQ.isError) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        Impossible de charger les e-mails de cet événement : {errorText(emailsQ.error || fieldsQ.error)}
      </Alert>
    );
  }

  const branding = brandingQ.data;

  return (
    <VStack align="stretch" spacing={6} data-testid="emails-tab">
      <Card>
        <CardHeader pb={0}>
          <Heading size="sm">Bandeau et pied de page</Heading>
          <Text fontSize="sm" color="gray.600">Communs à tous les e-mails de cet événement. PNG ou JPEG, 2 Mo maximum.</Text>
        </CardHeader>
        <CardBody>
          <Stack direction="column" spacing={6}>
            <BrandingSlot eventId={eventId} slot="header" label="Bandeau (en-tête)" url={branding?.effective_header_url} custom={!!branding?.header_image} />
            <BrandingSlot eventId={eventId} slot="footer" label="Pied de page" url={branding?.effective_footer_url} custom={!!branding?.footer_image} />
          </Stack>
        </CardBody>
      </Card>

      <Card>
        <CardHeader pb={2}>
          <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
            <Heading size="sm">E-mails de l'événement</Heading>
            <Menu>
              <MenuButton as={Button} size="sm" colorScheme="blue" rightIcon={<ChevronDownIcon />} isLoading={create.isPending} data-testid="email-new">
                Nouvel email
              </MenuButton>
              <MenuList>
                {NEW_EMAIL_KINDS.map((k) => (
                  <MenuItem key={k.kind} fontSize="sm" onClick={() => askNew(k.kind)} data-testid={`email-new-${k.kind}`}>{k.label}</MenuItem>
                ))}
              </MenuList>
            </Menu>
          </Flex>
        </CardHeader>
        <CardBody pt={0}>
          <Stack direction={{ base: 'column', md: 'row' }} spacing={2} wrap="wrap" mb={6} data-testid="email-list">
            {(emails || []).map((e) => (
              <Button
                key={e.id}
                variant={e.id === selectedId ? 'solid' : 'outline'}
                colorScheme={e.id === selectedId ? 'blue' : 'gray'}
                onClick={() => choose(e)}
                h="auto"
                py={2}
                px={4}
                justifyContent="flex-start"
                textAlign="left"
                data-testid={`email-item-${e.kind}`}
              >
                <Box>
                  <Text fontSize="sm" fontWeight="semibold">{e.name}</Text>
                  <Text fontSize="xs" fontWeight="normal" opacity={0.8}>{lastEdit(e)}</Text>
                </Box>
              </Button>
            ))}
          </Stack>

          {selected && draft && fields && (
            <Editor
              eventId={eventId}
              headerUrl={branding?.effective_header_url}
              footerUrl={branding?.effective_footer_url}
              email={selected}
              fields={fields}
              draft={draft}
              setDraft={setDraft}
              dirty={dirty}
              saving={save.isPending}
              onSave={() => save.mutate()}
              onReset={askReset}
              onTest={() => testSend.mutate()}
              testing={testSend.isPending}
              onDelete={SYSTEM_KINDS.includes(selected.kind) ? undefined : () => askDelete(selected)}
            />
          )}
          {selected && draft && !SYSTEM_KINDS.includes(selected.kind) && (
            <Box mt={6}>
              <SendPanel
                eventId={eventId}
                emailId={selected.id}
                subject={selected.subject}
                dirty={dirty}
                askConfirm={setConfirm}
              />
            </Box>
          )}
        </CardBody>
      </Card>
      <ConfirmDialog confirm={confirm} onClose={() => setConfirm(null)} />
    </VStack>
  );
};

export default EmailsTab;
